import { apiClient, getErrorMessage } from '../../utils/apiClient';
import { logger } from '../../utils/logger';
import { toast } from '../../utils/toast';
import {
  API_EDI_ITEMS_LIST,
  API_EDI_ITEMS_DETAIL,
  API_EDI_ITEMS_ALTERNATE_KEYS,
  API_EDI_ITEMS_PATCH,
  API_EDI_ITEMS_KEY_IDENTIFIERS,
  API_EDI_ITEMS_PACK_COMPOSITION,
  API_EDI_ITEMS_SOURCE_POS,
  API_EDI_ITEMS_BULK_PACK_PATTERNS,
  API_EDI_ITEMS_CARTON_USAGE,
  API_EDI_ITEMS_PACK_PATTERN,
  API_EDI_ITEMS_SELF_SERVE_OVERRIDE,
  API_EDI_ITEMS_CREATE,
  API_EDI_ITEMS_PREVIEW_KEY,
  API_EDI_ITEMS_SUPPRESS,
  API_EDI_ITEMS_BATCH_PATCH,
} from '../../constants/apiConstants';

// ============================================================================
// Types — mirror SmartMatrix.Domain.Edi.Messages.Masters.* output payloads.
// ============================================================================

export interface ItemAlternateKey {
  id: number;
  itemId: number;
  scope: string;
  scopeRefJson?: string | null;
  identifierType: string;
  identifierValue: string;
  dataSource?: string | null;
  isActive: boolean;
  createdAt?: string | null;
}

export interface Item {
  id: number;
  tenantId: number;
  canonicalIdentifier: string;
  description?: string | null;
  category?: string | null;
  length?: number | null;
  width?: number | null;
  height?: number | null;
  dimensionUnit?: string | null;
  netWeight?: number | null;
  /** Bare-product weight (shares weightUnit); customs/ASN. Must be ≤ netWeight. edi-item-master-net-net-weight. */
  netNetWeight?: number | null;
  weightUnit?: string | null;
  volume?: number | null;
  volumeUnit?: string | null;
  // Spec mlinkone-item-master-pack-defaults — item-level pack defaults (units).
  casePackQty?: number | null;
  innerPackQty?: number | null;
  // item-master-preferred-carton-type — per-pair preferred carton (detail only).
  preferredCartonTypeId?: number | null;
  preferredCartonTypeCode?: string | null;
  dataSourceOrigin?: string | null;
  lastObservedAt?: string | null;
  isActive: boolean;
  // Spec pack-analysis-prepack-item-master Req 13.1 — discriminator + Tier-2
  // metadata bag. itemType always present (mapper coerces missing → 'SKU'
  // for legacy BE responses). weightResolutionMode + metadataJson optional.
  itemType: string;
  weightResolutionMode?: string | null;
  metadataJson?: string | null;
  createdAt?: string | null;
  createdBy?: string | null;
  modifiedAt?: string | null;
  modifiedBy?: string | null;
  alternateKeys?: ItemAlternateKey[] | null;

  // edi-master-data-scoped-self-serve: populated only in scoped-projection mode
  // (per-pair rendering). null in legacy tenant-wide mode. description/category
  // carry the coalesced (overlay ?? canonical) values in projection mode.
  buyerPartyId?: number | null;
  supplierPartyId?: number | null;
  // edi-master-data-scoped-self-serve (manage affordance): true when the caller
  // holds item-master MANAGE for this row's pair (resolved per-party on the BE).
  // Drives the per-row self-serve override affordance — the party-blind login
  // permission list can't surface per-party EDI grants. undefined in legacy mode.
  canManage?: boolean;

  // mlinkone-item-master-create-and-scoped-edit (scoped-projection mode only):
  // displayCode = friendly SKU/UPC/description shown instead of the composed key;
  // isPairOwned = anchor at this pair (canonical editable) vs shared-scope
  // (overlay-only); isSuppressed = steward-deleted (normally filtered out).
  displayCode?: string | null;
  isPairOwned?: boolean;
  isSuppressed?: boolean;

  // item-master-identity-display — reference identity surfaced on the scoped list
  // projection (from the pair's alt-keys) so list-driven tools (bulk dims/weights)
  // can show Style/Color/Size without a per-row detail fetch. null in legacy mode.
  style?: string | null;
  color?: string | null;
  size?: string | null;

  // item-master-vendor-buyer-item-fields — buyer/vendor identifiers (per-pair
  // reference alt-keys, strictest-wins) + country of origin (per-pair alias
  // overlay). Populated in scoped-projection / detail mode; null in legacy mode.
  buyerItemNum?: string | null;
  vendorItemNum?: string | null;
  vendorStyle?: string | null;
  vendorColor?: string | null;
  vendorSize?: string | null;
  coo?: string | null;
}

// Mapper helper — coerces missing/null itemType to 'SKU' so legacy pre-spec
// BE responses don't break the FE list/detail rendering.
export function normalizeItem(raw: Partial<Item> & { itemType?: string | null }): Item {
  return {
    ...(raw as Item),
    itemType: raw.itemType && typeof raw.itemType === 'string' && raw.itemType.length > 0
      ? raw.itemType
      : 'SKU',
  };
}

export interface ItemListFilters {
  tenantId: number;
  q?: string;
  category?: string;
  hasDimensions?: boolean;
  hasAlternateKeyType?: string;
  hasAlternateKeyValue?: string;
  // Spec pack-analysis-prepack-item-master Req 13.4 — kind filter.
  // 'SKU' / 'PACK' / 'ALL' (sentinel = no kind filter applied).
  itemType?: string;
  pageIndex?: number;
  pageSize?: number;
  // edi-master-data-scoped-self-serve: when set, the BE returns the per-pair
  // scoped projection (legacy tenant-wide when both omitted).
  buyerPartyId?: number | null;
  supplierPartyId?: number | null;
}

/** edi-master-data-scoped-self-serve — per-pair self-serve display override. */
export interface ItemSelfServeOverrideRequest {
  buyerPartyId: number;
  supplierPartyId: number;
  descriptionOverride?: string | null;
  categoryOverride?: string | null;
  // item-master-vendor-buyer-item-fields — per-pair country of origin (full-state;
  // null/blank clears). Written as a separate coo-only alias update on the BE.
  coo?: string | null;
  modifiedBy?: string | null;
}

export interface ItemListResponse {
  items: Item[];
  pageIndex: number;
  pageSize: number;
  totalCount: number;
}

export interface ItemAlternateKeyListResponse {
  items: ItemAlternateKey[];
  pageIndex: number;
  pageSize: number;
  totalCount: number;
}

// Spec pack-analysis-item-master-pack-composition — drill-down payload for the
// PackInfoPanel sub-items table. Sub-items keyed by ItemKey from the historical
// PackMaster row's composition_json; resolved against sm_edi_item by canonical
// identifier match. Unresolved sub-items return ItemId/CanonicalIdentifier/
// Description as null per Req 2.3 (legitimate during partial ingest).
export interface ItemPackCompositionSubItem {
  itemKey: string;
  itemId?: number | null;
  canonicalIdentifier?: string | null;
  description?: string | null;
  quantity: number;
  uom: string;
  // pack-analysis-item-master-prepack-and-asns: base ratio component (= quantity / prepacksPerCarton); null when no reduction.
  baseRatioQuantity?: number | null;
  // Human-readable reference attributes (PO-declared composition). The UI prefers
  // these over the technical composed itemKey/canonicalIdentifier.
  sku?: string | null;
  upc?: string | null;
  ean?: string | null;
  style?: string | null;
  color?: string | null;
  size?: string | null;
}

// pack-analysis-item-master-prepack-and-asns: compact carton ref for the per-variant "packed in L×W×H" label (R7).
export interface ItemPackCompositionCartonRef {
  id: number;
  description?: string | null;
  length?: number | null;
  width?: number | null;
  height?: number | null;
  dimensionUnit?: string | null;
}

// pack-analysis-item-master-prepack-and-asns: one observed composition variant (R6).
export interface ItemPackCompositionVariant {
  packMasterId: number;
  compositionSignature?: string | null;
  frequencyCount: number;
  prepacksPerCarton: number;
  subItems: ItemPackCompositionSubItem[];
  representativeCartonType?: ItemPackCompositionCartonRef | null;
}

export interface ItemPackComposition {
  itemId: number;
  prepackCode: string;
  packMasterId?: number | null;
  frequency?: number | null;
  lastSeen?: string | null;
  subItems: ItemPackCompositionSubItem[];
  // item-master-prepack-enhancements — current PO version's composition differs from
  // the previous version's (PO-declared path). Drives the "composition changed" badge.
  compositionChanged?: boolean;
  previousVersionNo?: number | null;
  // pack-analysis-item-master-prepack-and-asns — unified base ratio (R1/R2/R6).
  prepacksPerCarton?: number;          // 1 = no reduction
  ratioSource?: string | null;         // "derived"
  ratioConfidence?: string | null;     // none|low|medium|high (corroboration)
  variantCount?: number;
  variants?: ItemPackCompositionVariant[];
}

export interface ItemPackCompositionResponse {
  data: ItemPackComposition | null;
  note?: string | null;
}

// mlinkone-item-master-carton-usage — read-only Actual-Packing carton distribution for one item + pair.
export interface CartonUsageRow {
  cartonTypeId?: number | null;
  /** CartonType.Description; the sentinel '__deleted__' when the carton type is soft-deleted/unresolved. */
  cartonName: string;
  unitsPerCarton: number;
  frequency: number;
  lastPackedAt?: string | null;
  /** The "Most used" (frequency-first) winner — the carton Suggest defaults to. */
  isWinner: boolean;
  /** The "Recent-weighted" (time-decay) winner, using the buyer's half-life. May coincide with isWinner. */
  isRecencyWinner: boolean;
  isRemainder: boolean;
}
export interface CartonUsage {
  itemId: number;
  /** True when buyer/supplier scope is missing — the panel prompts to pick a pair. */
  scopeRequired: boolean;
  lastCommittedAt?: string | null;
  rows: CartonUsageRow[];
  /** Req 8.2 — committed Actual Packing list numbers (PL#s) that built this pattern (the primary carton signal). */
  contributingPackingListNumbers?: string[];
  /** Req 8.2 — contributing PO numbers. */
  contributingPoNumbers?: string[];
}

// mlinkone-packing-evidence-precedence Req 9 — result of a Clear-pattern reset.
export interface ClearPackPatternResult {
  itemId: number;
  actualRowsCleared: number;
  asnPatternRowsCleared: number;
}

// item-master-prepack-enhancements — Source POs trace panel.
export interface ItemSourcePosRow {
  poId: number;
  poNumber: string;
  versionNo: number;
  isCurrent: boolean;
  sentDate?: string | null;
}
export interface ItemSourcePos {
  itemId: number;
  prepackCode?: string | null;
  pos: ItemSourcePosRow[];
}
export interface ItemSourcePosResponse {
  data: ItemSourcePos | null;
  note?: string | null;
}

// pack-analysis-item-master-bulk-pack-info — one pure-BULK pattern: a distinct
// per-carton quantity, its mode carton type, and carton count.
export interface BulkPackPattern {
  packMasterId: number;
  quantityPerCarton: number;
  uom: string;
  cartonCount: number;
  lastSeenAt?: string | null;
  representativeCartonType?: ItemPackCompositionCartonRef | null;
}

export interface BulkPackPatterns {
  itemId: number;
  itemKey: string;
  totalCartonCount: number;
  lastSeen?: string | null;
  patterns: BulkPackPattern[];
}

export interface BulkPackPatternsResponse {
  data: BulkPackPatterns | null;
  note?: string | null;
}

// Patch body — only non-null fields are applied. Identity fields are
// intentionally absent; sending them would trigger Field_Locked on backend.
export interface ItemPatchRequest {
  description?: string | null;
  category?: string | null;
  length?: number | null;
  width?: number | null;
  height?: number | null;
  dimensionUnit?: string | null;
  netWeight?: number | null;
  /** Bare-product weight (shares weightUnit); customs/ASN. Must be ≤ netWeight. edi-item-master-net-net-weight. */
  netNetWeight?: number | null;
  weightUnit?: string | null;
  volume?: number | null;
  volumeUnit?: string | null;
  // Spec mlinkone-item-master-pack-defaults — item-level pack defaults (units).
  casePackQty?: number | null;
  innerPackQty?: number | null;
  // item-master-preferred-carton-type — per-pair preferred carton (bulk SKU). Null =
  // leave unchanged; list "preferredCartonTypeId" in clearFields to clear it.
  preferredCartonTypeId?: number | null;
  // Numeric null reads as "leave unchanged", so emptying a box lists the field name
  // here to force the column back to NULL (item-master null-clobber fix). Accepted:
  // length, width, height, netWeight, volume, casePackQty, innerPackQty,
  // preferredCartonTypeId.
  clearFields?: string[];
  // PACK weight resolution mode ('DIRECT' | 'SUM_OF_SUBITEMS'). Applied to PACK items only
  // (a DB CHECK keeps SKU rows null). Null = leave unchanged.
  weightResolutionMode?: string | null;
  // mlinkone-item-master-create-and-scoped-edit — active pair: canonical edit is
  // gated per-party + only allowed when the item is owned by this pair.
  buyerPartyId?: number | null;
  supplierPartyId?: number | null;

  // item-master-manual-prepack-and-identifier-edit — when true, the BE reconciles
  // the item's NON-KEY reference identifiers to these values (null = clear), enforcing
  // immutability of the canonical key by recomposition (a key-moving change is rejected
  // with Key_Would_Change). Omit / false to leave identifiers untouched.
  editIdentifiers?: boolean;
  sku?: string | null;
  upc?: string | null;
  ean?: string | null;
  style?: string | null;
  color?: string | null;
  size?: string | null;
  // item-master-vendor-buyer-item-fields — buyer/vendor reference identifiers,
  // reconciled with the above when editIdentifiers is true (null = clear). Not
  // canonical-key components, so they never trigger Key_Would_Change.
  buyerItemNum?: string | null;
  vendorItemNum?: string | null;
  vendorStyle?: string | null;
  vendorColor?: string | null;
  vendorSize?: string | null;
}

// edi-item-master-bulk-dims-weight-entry — fast bulk dims/weights grid save.
export interface ItemBatchPatchRow {
  id: number;
  length?: number | null;
  width?: number | null;
  height?: number | null;
  dimensionUnit?: string | null;
  netWeight?: number | null;
  /** Bare-product weight (shares weightUnit); customs/ASN. Must be ≤ netWeight. edi-item-master-net-net-weight. */
  netNetWeight?: number | null;
  weightUnit?: string | null;
  /** Numeric fields the user EMPTIED (a blanked cell) — forced to NULL. A null value above
   * reads as "leave unchanged", so a cleared cell must be named here. Tokens match the BE
   * ClearableFields (e.g. 'length','netWeight','netNetWeight'). */
  clearFields?: string[];
}

export interface ItemBatchPatchRequest {
  buyerPartyId: number;
  supplierPartyId: number;
  items: ItemBatchPatchRow[];
}

export interface ItemBatchPatchResult {
  updatedCount: number;
  results: { id: number; status: string }[];
}

// mlinkone-item-master-create-and-scoped-edit — manual create + key preview.
export interface ItemCreateRequest {
  buyerPartyId: number;
  supplierPartyId: number;
  upc?: string | null;
  sku?: string | null;
  style?: string | null;
  ean?: string | null;
  // item-master-prepack-enhancements — Color/Size feed key composition (when the
  // buyer keys on them) and are stored as reference attributes regardless.
  color?: string | null;
  size?: string | null;
  description?: string | null;
  category?: string | null;
  length?: number | null;
  width?: number | null;
  height?: number | null;
  dimensionUnit?: string | null;
  netWeight?: number | null;
  /** Bare-product weight (shares weightUnit); customs/ASN. Must be ≤ netWeight. edi-item-master-net-net-weight. */
  netNetWeight?: number | null;
  weightUnit?: string | null;
  volume?: number | null;
  volumeUnit?: string | null;
  casePackQty?: number | null;
  innerPackQty?: number | null;
  // item-master-preferred-carton-type — optional preferred carton (bulk SKU) for the pair.
  preferredCartonTypeId?: number | null;

  // item-master-manual-prepack-and-identifier-edit (Phase 2) — manual prepack create.
  // itemType 'PACK' requires prepackCode (key = PREPACK:<code>); components = declared BOM.
  itemType?: string | null;
  prepackCode?: string | null;
  weightResolutionMode?: string | null;
  components?: ItemCreateComponent[] | null;
}

/** One declared prepack component: an existing in-scope item's canonical key + units per prepack. */
export interface ItemCreateComponent {
  componentItemKey: string;
  quantity: number;
  uom?: string | null;
}

export interface ItemPreviewKeyRequest {
  buyerPartyId: number;
  supplierPartyId: number;
  upc?: string | null;
  sku?: string | null;
  style?: string | null;
  ean?: string | null;
  color?: string | null;
  size?: string | null;
}

export interface ItemCreateResult {
  itemId: number;
  composedKey?: string | null;
  alreadyExists: boolean;
}

// Backend Result<T> wrapper. Identical shape used everywhere — share the
// type via the apiClient or a shared types file later if needed.
interface ResultEnvelope<T> {
  succeeded: boolean;
  statusCode?: number;
  messages?: string[];
  data?: T;
}

// ============================================================================
// Service
// ============================================================================

export const itemMasterService = {
  async listItems(filters: ItemListFilters): Promise<ItemListResponse> {
    try {
      const response = await apiClient.get<ResultEnvelope<ItemListResponse>>(
        API_EDI_ITEMS_LIST,
        {
          params: {
            tenantId: filters.tenantId,
            q: filters.q || undefined,
            category: filters.category || undefined,
            hasDimensions: filters.hasDimensions,
            hasAlternateKeyType: filters.hasAlternateKeyType || undefined,
            hasAlternateKeyValue: filters.hasAlternateKeyValue || undefined,
            itemType: filters.itemType || undefined,
            pageIndex: filters.pageIndex ?? 1,
            pageSize: filters.pageSize ?? 50,
            buyerPartyId: filters.buyerPartyId ?? undefined,
            supplierPartyId: filters.supplierPartyId ?? undefined,
          },
        }
      );
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', ') || 'Failed to list items');
      const data = response.data.data ?? { items: [], pageIndex: 1, pageSize: 50, totalCount: 0 };
      // Normalize each row's itemType for legacy pre-spec BE responses.
      return { ...data, items: (data.items || []).map(normalizeItem) };
    } catch (error) {
      logger.error('Failed to list items', error);
      throw error;
    }
  },

  async getItem(
    tenantId: number,
    id: number,
    scope?: { buyerPartyId?: number | null; supplierPartyId?: number | null }
  ): Promise<Item> {
    try {
      const response = await apiClient.get<ResultEnvelope<{ item: Item | null }>>(
        API_EDI_ITEMS_DETAIL(id),
        // edi-master-data-scoped-self-serve: scope params authorize party-scoped
        // EDI readers on the detail endpoint (the handler still returns canonical).
        { params: { tenantId, buyerPartyId: scope?.buyerPartyId ?? undefined, supplierPartyId: scope?.supplierPartyId ?? undefined } }
      );
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', ') || 'Failed to get item');
      const item = response.data.data?.item;
      if (!item) throw new Error('Item not found');
      return normalizeItem(item);
    } catch (error) {
      logger.error('Failed to get item', error);
      throw error;
    }
  },

  async listAlternateKeys(
    tenantId: number,
    itemId: number,
    pageIndex = 1,
    pageSize = 50
  ): Promise<ItemAlternateKeyListResponse> {
    try {
      const response = await apiClient.get<ResultEnvelope<ItemAlternateKeyListResponse>>(
        API_EDI_ITEMS_ALTERNATE_KEYS(itemId),
        { params: { tenantId, pageIndex, pageSize } }
      );
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', ') || 'Failed to list alt keys');
      return response.data.data ?? { items: [], pageIndex, pageSize, totalCount: 0 };
    } catch (error) {
      logger.error('Failed to list item alternate keys', error);
      throw error;
    }
  },

  // item-master-prepack-enhancements — in-scope POs/versions declaring a prepack code
  // (the lazy "Source POs" trace panel; mirrors getPackComposition).
  async getSourcePos(
    tenantId: number,
    itemId: number,
    signal?: AbortSignal,
    scope?: { buyerPartyId?: number | null; supplierPartyId?: number | null }
  ): Promise<ItemSourcePosResponse> {
    const response = await apiClient.get<ResultEnvelope<{ data: ItemSourcePos | null; note?: string | null }>>(
      API_EDI_ITEMS_SOURCE_POS(itemId),
      { params: { tenantId, buyerPartyId: scope?.buyerPartyId ?? undefined, supplierPartyId: scope?.supplierPartyId ?? undefined }, signal }
    );
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.join(', ') || 'Failed to fetch source POs');
    }
    const inner = response.data.data ?? { data: null, note: null };
    return { data: inner.data ?? null, note: inner.note ?? null };
  },

  async getPackComposition(
    tenantId: number,
    itemId: number,
    signal?: AbortSignal,
    scope?: { buyerPartyId?: number | null; supplierPartyId?: number | null }
  ): Promise<ItemPackCompositionResponse> {
    const response = await apiClient.get<ResultEnvelope<{ data: ItemPackComposition | null; note?: string | null }>>(
      API_EDI_ITEMS_PACK_COMPOSITION(itemId),
      // edi-master-data-scoped-self-serve: scope params authorize party-scoped readers.
      { params: { tenantId, buyerPartyId: scope?.buyerPartyId ?? undefined, supplierPartyId: scope?.supplierPartyId ?? undefined }, signal }
    );
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.join(', ') || 'Failed to fetch pack composition');
    }
    const inner = response.data.data ?? { data: null, note: null };
    return { data: inner.data ?? null, note: inner.note ?? null };
  },

  // pack-analysis-item-master-bulk-pack-info — historical pure-BULK pack patterns
  // for a SKU. itemKey is the FE-resolved composed canonical key (resolveCanonicalKey).
  async getBulkPackPatterns(
    tenantId: number,
    itemId: number,
    itemKey: string,
    scope?: { buyerPartyId?: number | null; supplierPartyId?: number | null },
    signal?: AbortSignal
  ): Promise<BulkPackPatternsResponse> {
    const response = await apiClient.get<ResultEnvelope<{ data: BulkPackPatterns | null; note?: string | null }>>(
      API_EDI_ITEMS_BULK_PACK_PATTERNS(itemId),
      { params: { tenantId, itemKey, buyerPartyId: scope?.buyerPartyId ?? undefined, supplierPartyId: scope?.supplierPartyId ?? undefined }, signal }
    );
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.join(', ') || 'Failed to fetch bulk pack patterns');
    }
    const inner = response.data.data ?? { data: null, note: null };
    return { data: inner.data ?? null, note: inner.note ?? null };
  },

  // mlinkone-item-master-carton-usage — read-only carton distribution for a SKU under a (buyer, supplier)
  // pair. The BE resolves the item's composed key server-side (no itemKey param needed). Returns
  // scopeRequired=true when a pair isn't set; empty rows when the item has no committed packing history.
  async getCartonUsage(
    tenantId: number,
    itemId: number,
    scope?: { buyerPartyId?: number | null; supplierPartyId?: number | null },
    signal?: AbortSignal
  ): Promise<CartonUsage | null> {
    const response = await apiClient.get<ResultEnvelope<{ data: CartonUsage | null }>>(
      API_EDI_ITEMS_CARTON_USAGE(itemId),
      { params: { tenantId, buyerPartyId: scope?.buyerPartyId ?? undefined, supplierPartyId: scope?.supplierPartyId ?? undefined }, signal }
    );
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.join(', ') || 'Failed to fetch carton usage');
    }
    return response.data.data?.data ?? null;
  },

  // mlinkone-packing-evidence-precedence Req 9 — CLEAR (reset) an item's learned packing pattern for a
  // (buyer, supplier) pair. Soft-deletes the Actual aggregate + the item's BULK ASN patterns (reset, not
  // source purge — re-commit / re-ingest rebuilds). Per-party MANAGE gated server-side. Idempotent.
  async clearPackPattern(
    tenantId: number,
    itemId: number,
    scope: { buyerPartyId: number; supplierPartyId: number },
  ): Promise<ClearPackPatternResult> {
    const response = await apiClient.delete<ResultEnvelope<ClearPackPatternResult>>(
      API_EDI_ITEMS_PACK_PATTERN(itemId),
      { params: { tenantId, buyerPartyId: scope.buyerPartyId, supplierPartyId: scope.supplierPartyId } },
    );
    if (!response.data.succeeded) {
      throw new Error(response.data.messages?.join(', ') || 'Failed to clear pack pattern');
    }
    return response.data.data ?? { itemId, actualRowsCleared: 0, asnPatternRowsCleared: 0 };
  },

  // item-master-manual-prepack-and-identifier-edit — resolve the buyer's key delimiter
  // for this pair. The edit dialog splits the item's canonical key on it to find which
  // identifier values formed the key (those are the immutable key components, locked in
  // the UI). UX hint only — the BE recomposition guard is the binding guarantee. Failure
  // → the platform default '||'. mixKind = 'BULK' (SKU items) | 'PREPACK'.
  // item-master-manual-prepack-and-identifier-edit — the identifier types that form this
  // item's canonical key for the pair (BE-computed from the buyer's profile's fired level,
  // incl. an empty key component). The edit dialog locks these. Same per-party read gate as
  // the editor, so locking never depends on a separate config permission. Failure → [].
  async getKeyIdentifierTypes(
    tenantId: number, id: number, buyerPartyId?: number | null, supplierPartyId?: number | null,
  ): Promise<Set<string>> {
    try {
      const response = await apiClient.get<ResultEnvelope<{ types?: string[] }>>(
        API_EDI_ITEMS_KEY_IDENTIFIERS(id),
        { params: { tenantId, buyerPartyId: buyerPartyId ?? undefined, supplierPartyId: supplierPartyId ?? undefined } },
      );
      if (!response.data.succeeded) return new Set();
      return new Set(response.data.data?.types ?? []);
    } catch { return new Set(); }
  },

  async patchItem(
    tenantId: number,
    id: number,
    request: ItemPatchRequest,
    // Callers that surface the error themselves (e.g. the edit dialog renders it
    // inline) pass suppressErrorToast to avoid a duplicate toast outside the dialog.
    options?: { suppressErrorToast?: boolean }
  ): Promise<Item> {
    try {
      const response = await apiClient.patch<ResultEnvelope<{ item: Item | null }>>(
        API_EDI_ITEMS_PATCH(id),
        request,
        { params: { tenantId } }
      );
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', ') || 'Failed to update item');
      const item = response.data.data?.item;
      if (!item) throw new Error('Item not found after update');
      toast.success('Item updated');
      return item;
    } catch (error) {
      const msg = getErrorMessage(error);
      if (!options?.suppressErrorToast) toast.error(`Failed to update item: ${msg}`);
      throw error;
    }
  },

  /**
   * edi-item-master-bulk-dims-weight-entry — the fast bulk dims/weights grid save.
   * Applies dims + net weight to N pair-owned items in one call. The BE checks the
   * per-party MANAGE gate once and reports each item (`updated`/`no_change`/`not_owned`/
   * `not_found`). Patch-non-null, so blank cells never clear a stored value. Throws on
   * a hard failure (e.g. Not_Authorized).
   */
  async batchPatchItems(tenantId: number, request: ItemBatchPatchRequest): Promise<ItemBatchPatchResult> {
    try {
      const response = await apiClient.post<ResultEnvelope<ItemBatchPatchResult>>(
        API_EDI_ITEMS_BATCH_PATCH,
        request,
        { params: { tenantId } }
      );
      if (!response.data.succeeded) throw new Error(response.data.messages?.join(', ') || 'Failed to update items');
      const data = response.data.data ?? { updatedCount: 0, results: [] };
      toast.success(`Updated ${data.updatedCount} item(s)`);
      return data;
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to update items: ${msg}`);
      throw error;
    }
  },

  /**
   * edi-master-data-scoped-self-serve — upsert a per-pair display override
   * (description / category) for an item. Both fields blank clears it. Throws
   * on failure (incl. Not_Authorized -2761 when the caller lacks
   * item-master.manage for the pair).
   */
  async selfServeOverride(
    tenantId: number,
    id: number,
    request: ItemSelfServeOverrideRequest,
  ): Promise<void> {
    try {
      const response = await apiClient.put<ResultEnvelope<unknown>>(
        API_EDI_ITEMS_SELF_SERVE_OVERRIDE(id),
        request,
        { params: { tenantId } },
      );
      if (!response.data.succeeded) {
        throw new Error(response.data.messages?.join(', ') || 'Failed to save item override');
      }
      toast.success('Saved for this scope');
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to save: ${msg}`);
      throw error;
    }
  },

  /**
   * mlinkone-item-master-create-and-scoped-edit — compose-only key preview for the
   * create dialog. Returns the composed key (or null if the profile yields nothing).
   * Never toasts — it backs a live, debounced preview. Returns null on any failure.
   */
  async previewItemKey(tenantId: number, request: ItemPreviewKeyRequest): Promise<string | null> {
    try {
      const response = await apiClient.post<ResultEnvelope<{ composedKey?: string | null }>>(
        API_EDI_ITEMS_PREVIEW_KEY,
        request,
        { params: { tenantId } },
      );
      if (!response.data.succeeded) return null;
      return response.data.data?.composedKey ?? null;
    } catch {
      return null;
    }
  },

  /**
   * mlinkone-item-master-create-and-scoped-edit — manual item creation. Returns
   * `alreadyExists: true` (no throw) when the composed key already exists at the
   * pair, so the caller can surface a friendly message.
   */
  async createItem(tenantId: number, request: ItemCreateRequest): Promise<ItemCreateResult> {
    try {
      const response = await apiClient.post<ResultEnvelope<{ itemId: number; composedKey?: string | null }>>(
        API_EDI_ITEMS_CREATE,
        request,
        { params: { tenantId } },
      );
      const env = response.data;
      // -2421 Already_Exists is a "soft" outcome (Result.Fail with data).
      if (!env.succeeded && env.statusCode === -2421) {
        return { itemId: env.data?.itemId ?? 0, composedKey: env.data?.composedKey ?? null, alreadyExists: true };
      }
      if (!env.succeeded) {
        throw new Error(env.messages?.join(', ') || 'Failed to create item');
      }
      toast.success('Item created');
      return { itemId: env.data?.itemId ?? 0, composedKey: env.data?.composedKey ?? null, alreadyExists: false };
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to create item: ${msg}`);
      throw error;
    }
  },

  /**
   * mlinkone-item-master-create-and-scoped-edit — steward "delete" via suppression
   * (non-destructive). `suppress=false` restores.
   */
  async suppressItem(
    tenantId: number,
    id: number,
    scope: { buyerPartyId: number; supplierPartyId: number },
    suppress = true,
  ): Promise<void> {
    try {
      const response = await apiClient.delete<ResultEnvelope<unknown>>(
        API_EDI_ITEMS_SUPPRESS(id),
        { params: { tenantId, buyerPartyId: scope.buyerPartyId, supplierPartyId: scope.supplierPartyId, suppress } },
      );
      if (!response.data.succeeded) {
        throw new Error(response.data.messages?.join(', ') || 'Failed to delete item');
      }
      toast.success(suppress ? 'Item deleted' : 'Item restored');
    } catch (error) {
      const msg = getErrorMessage(error);
      toast.error(`Failed to delete item: ${msg}`);
      throw error;
    }
  },
};
